import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import AppShell from "./components/layout/AppShell";
import PageSkeleton from "./components/layout/PageSkeleton";
import ToastHost from "./components/ui/toast";
import { AuthProvider, useAuth } from "./store/AuthContext";

const Login = lazy(() => import("./pages/Login"));
const Placeholder = lazy(() => import("./pages/MigrationPlaceholder"));
// 旗舰页（v2-flagship-pages 批次）：已迁移完成的页面直接替换对应路由的 Placeholder
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Purchase = lazy(() => import("./pages/Purchase"));
const Approval = lazy(() => import("./pages/Approval"));
const Production = lazy(() => import("./pages/Production"));
const Workshop = lazy(() => import("./pages/Workshop"));
const Equipment = lazy(() => import("./pages/Equipment"));
const Quality = lazy(() => import("./pages/Quality"));
// Phase 2 批 A（2026-09-23）：生产制造域补齐（安灯/上料防错/SOP）+ 质量与追溯域补齐（追溯）
const Andon = lazy(() => import("./pages/Andon"));
const MaterialVerify = lazy(() => import("./pages/MaterialVerify"));
const Sop = lazy(() => import("./pages/Sop"));
const Traceability = lazy(() => import("./pages/Traceability"));
// Phase 2 批 B（2026-09-23）：销售与客户域（销售订单/销售CRM/客户/报价）
const Sales = lazy(() => import("./pages/Sales"));
const SalesCRM = lazy(() => import("./pages/SalesCRM"));
const Customer = lazy(() => import("./pages/Customer"));
const Pricing = lazy(() => import("./pages/Pricing"));
// Phase 2 批 C（2026-09-23）：财务 + 供应链剩余 + 工程数据（财务/盘点/请购/BOM）
const Finance = lazy(() => import("./pages/Finance"));
const StockCheck = lazy(() => import("./pages/StockCheck"));
const PurchaseReq = lazy(() => import("./pages/PurchaseReq"));
const Bom = lazy(() => import("./pages/Bom"));
// Phase 2 批 D（2026-09-23）：协同办公（消息通知/提醒配置/问题反馈）
const Notifications = lazy(() => import("./pages/Notifications"));
const Reminder = lazy(() => import("./pages/Reminder"));
const Feedback = lazy(() => import("./pages/Feedback"));
// Phase 2 批 E（2026-09-23）：治理后台（组织权限/系统设置/前端监控）
const Iam = lazy(() => import("./pages/Iam"));
const AIConfig = lazy(() => import("./pages/AIConfig"));
const Monitoring = lazy(() => import("./pages/Monitoring"));
// Phase 2 批 F（2026-09-23）：协同办公剩余（我的工作区/模板管理）
const Workspace = lazy(() => import("./pages/Workspace"));
const WorkspaceAdmin = lazy(() => import("./pages/WorkspaceAdmin"));
// 2026-09-25：项目管理（PM/PMO）—— 工作台有了项目指标，这里补上「点得进去」的明细页
const ProjectPage = lazy(() => import("./pages/Project"));
// Phase 2 批 G（2026-09-23）：最后三页（智能问数/扫码出入库/编码规则）→ v2 全量覆盖
const ChatBI = lazy(() => import("./pages/ChatBI"));
const MobileScan = lazy(() => import("./pages/MobileScan"));
const BomCoderRules = lazy(() => import("./pages/BomCoderRules"));
// 设计系统自检页：无需登录（本地没有后端时也能走查视觉），仅在 /__design 与 /__heavy 可达
const DesignSystemPreview = lazy(() => import("./pages/DesignSystemPreview"));
const HeavyPreview = lazy(() => import("./pages/HeavyPreview"));
// 大屏模式：独立全屏路由（不套壳层导航，供车间/展厅投屏）
const FullScreenDashboard = lazy(() => import("./components/FullScreenDashboard"));

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthed, loading } = useAuth();
  if (loading) return <PageSkeleton rows={4} />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * 壳内菜单路径（与 config/roleMenus 的菜单 key 一一对应，无 HR —— HR 走独立前端 hr.co-seek.cn）。
 * 迁移完成的页面在下方 `PAGE_ROUTES` 里把 Placeholder 换成真实页面组件即可，
 * 未迁移的继续落占位页，保证 v2 全站可导航、可与旧前端对照。
 */
const SHELL_PATHS = [
  "inventory",
  "stockcheck",
  "purchase",
  "purchase-req",
  "sales",
  "sales-crm",
  "project",
  "workspace",
  "workspace-admin",
  "customer",
  "pricing",
  "production",
  "workshop",
  "equipment",
  "quality",
  "sop",
  "andon",
  "material-verify",
  "mobile-scan",
  "chatbi",
  "bom",
  "bom-coder-rules",
  "traceability",
  "finance",
  "approval",
  "reminder",
  "notifications",
  "iam",
  "ai-config",
  "monitoring",
  "feedback",
];

/** 已迁移完成的页面：未登记的一律落 MigrationPlaceholder，保证全站可导航 */
const MIGRATED_PAGES: Record<string, ReactNode> = {
  inventory: <Inventory />,
  purchase: <Purchase />,
  approval: <Approval />,
  production: <Production />,
  workshop: <Workshop />,
  equipment: <Equipment />,
  quality: <Quality />,
  andon: <Andon />,
  "material-verify": <MaterialVerify />,
  sop: <Sop />,
  traceability: <Traceability />,
  sales: <Sales />,
  "sales-crm": <SalesCRM />,
  customer: <Customer />,
  pricing: <Pricing />,
  finance: <Finance />,
  stockcheck: <StockCheck />,
  "purchase-req": <PurchaseReq />,
  bom: <Bom />,
  notifications: <Notifications />,
  reminder: <Reminder />,
  feedback: <Feedback />,
  iam: <Iam />,
  "ai-config": <AIConfig />,
  monitoring: <Monitoring />,
  workspace: <Workspace />,
  "workspace-admin": <WorkspaceAdmin />,
  project: <ProjectPage />,
  chatbi: <ChatBI />,
  "mobile-scan": <MobileScan />,
  "bom-coder-rules": <BomCoderRules />,
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* 设计走查页（公开）：/__design 令牌与原语，/__heavy 重组件 */}
            <Route path="/__design" element={<DesignSystemPreview />} />
            <Route path="/__heavy" element={<HeavyPreview />} />

            {/* 大屏模式（独立全屏路由，不套壳层导航） */}
            <Route
              path="/screen"
              element={
                <RequireAuth>
                  <FullScreenDashboard />
                </RequireAuth>
              }
            />

            {/* 主壳层：含侧栏/顶栏/面包屑/页签，页面按角色二次校验（403 兜底） */}
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<Dashboard />} />
              {SHELL_PATHS.map((path) => (
                <Route key={path} path={path} element={MIGRATED_PAGES[path] ?? <Placeholder />} />
              ))}
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <ToastHost />
      </AuthProvider>
    </BrowserRouter>
  );
}
